import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { getMethod, putMethod } from "../../services/apiService";
import cameraOfflineIcon from '../../assets/images/icon/camera_offline.svg';
import cameraOnlineIcon from '../../assets/images/icon/camera_online.svg';
import { useParams, useNavigate } from "react-router-dom";
import Loading from "../loading";
import { IconDeviceCctvOff } from '@tabler/icons-react';
import { IconDeviceCctv } from '@tabler/icons-react';

export default function DeviceTable({

  rowData,
  cameraStatus,

}: any) {

  let { id } = useParams();

  const handleCheckStatus = (id: number) => {
    let a = cameraStatus.find((d: any) => d.id === id);
    return a?.statusText;
  };
  const [cameraLocationsMap, setCameraLocationsMap] = React.useState<{ [cameraId: number]: number[] }>({});
  const [isCameraLocationsLoaded, setIsCameraLocationsLoaded] = React.useState(false);
  const [allLocations, setAllLocations] = React.useState<any>([]);
  const hasFetchedLocations = React.useRef(false);

  React.useEffect(() => {
    if (!rowData?.length || hasFetchedLocations.current) return;

    hasFetchedLocations.current = true; // mark as fetched
    setIsCameraLocationsLoaded(false); 

    const fetchAllLocations = async () => {
      try {
        const results: Record<number, number[]> = {};

        
        // Fetch all cameras in parallel
        await Promise.all(
          rowData.map(async (row: any) => {
            const ids: number[] = await fetchCameraLocations(row.id);
            const filteredIds = Array.from(new Set(ids.filter((id: number) => id !== 1)));
            results[row.id] = filteredIds;
          })
        );

        setCameraLocationsMap(results);
        setIsCameraLocationsLoaded(true);
      } catch (err) {
        console.error("Error fetching camera locations:", err);
        setIsCameraLocationsLoaded(true);
      }
    };

    fetchAllLocations();
  }, [rowData]);


  // Async function that returns the location IDs
  const fetchCameraLocations = async (cameraId: number) => {
    const getSiteID = localStorage.getItem("siteId") ?? id;
    const resp = await getMethod(`site/config/${getSiteID}/channel/${cameraId}/locations`);
    return resp?.data?.result?.[0]?.locationIds ?? [];
  };

  const fetchAllCameraLocations = async () => {
    const getSiteID = localStorage.getItem("siteId") ?? id;
    const resp = await getMethod(`site/${getSiteID}/getalllocations`);
    setAllLocations(resp?.data?.result ?? []);
    if(resp?.data?.result.length == 0)
      setIsCameraLocationsLoaded(true);
  };

  const locationNameMap = React.useMemo(() => {
    const map: Record<number, string> = {};
    if (Array.isArray(allLocations)) {
      allLocations.forEach((loc: any) => {
        map[loc.id] = loc.name;
      });
    }
    return map;
  }, [allLocations]);


  React.useEffect(() => {
    fetchAllCameraLocations();
  }, [])


  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        sx={{
          width: "100%",
          mb: 2,
          background: "transparent",
          border: "1px solid #313838",
          borderRadius: "10px",
        }}
      >
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"

          >

            <TableHead>
              <TableRow style={{ borderBottom: "2px solid #313838" }}>
                <TableCell sx={{ color: "#E2E3E3", borderColor: "#313838", fontWeight: "500", fontSize: "16px" }}>Device Name</TableCell>
                <TableCell sx={{ color: "#E2E3E3", borderColor: "#313838", fontWeight: "500", fontSize: "16px" }}>Status</TableCell>
                <TableCell sx={{ color: "#E2E3E3", borderColor: "#313838", fontWeight: "500", fontSize: "16px" }}>Location</TableCell>
                <TableCell sx={{ color: "#E2E3E3", borderColor: "#313838", fontWeight: "500", fontSize: "16px" }}>Model</TableCell>
                <TableCell sx={{ color: "#E2E3E3", borderColor: "#313838", fontWeight: "500", fontSize: "16px" }}>IP</TableCell>
                <TableCell sx={{ color: "#E2E3E3", borderColor: "#313838", fontWeight: "500", fontSize: "16px" }}>Type</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isCameraLocationsLoaded && rowData?.map((row: any, index: any) => {

                return (
                  <TableRow
                    sx={{ cursor: "pointer", border: "1px solid #5A5F60" }}
                  >

                    <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>{row?.name}</TableCell>
                    <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>{handleCheckStatus(row?.id) === "ONLINE"
                      ? <IconDeviceCctv stroke={1.4} size={20} color="#07A25C" aria-label=""/>
                      // <img src={cameraOnlineIcon} alt="" width={20} height={20} />
                      : <IconDeviceCctvOff stroke={1.4} size={20} aria-label="" color="#FB462E" />
                      // <img src={cameraOfflineIcon} alt="" width={20} height={20} />
                      }
                      </TableCell>
                    <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>
                      {cameraLocationsMap[row?.id]?.length && Object.keys(locationNameMap).length
                        ? ["All Cameras", ...cameraLocationsMap[row.id]
                          .map((locId) => locationNameMap[locId])]
                          .join(", ")
                        : "All Cameras"}
                    </TableCell>

                    <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>{row?.model}</TableCell>
                    <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>{row?.ip}</TableCell>
                    <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>
                      {row?.configurationType === 0
                        ? "Fixed"
                        : row?.configurationType ===
                          1
                          ? "PTZ"
                          : row?.configurationType ===
                            2
                            ? "360 Degree"
                            : row?.configurationType ===
                              3
                              ? "Zoom Camera"
                              : row?.configurationType ===
                                4
                                ? "Thermal Camera"
                                : row?.configurationType ===
                                  5
                                  ? "Drone Camera"
                                  : ""}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!isCameraLocationsLoaded &&
                // <Box
                //   sx={{
                //     display: "flex",
                //     justifyContent: "center",
                //     alignItems: "center",
                //     height: "calc(100%)",
                //   }}
                // >
                //   <Loading />
                // </Box>
                <TableRow>
                  <TableCell colSpan={8} style={{ color: '#B4B7B8', textAlign: 'center' }}><Loading /></TableCell>
                </TableRow>
                }
              {rowData?.length === 0 && isCameraLocationsLoaded? (
                <TableRow>
                  <TableCell colSpan={8} style={{ color: '#B4B7B8', textAlign: 'center' }}>No data found!</TableCell>
                </TableRow>
              ) : (
                ""
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
